import {
  DEFAULT_BACKEND_FETCH_TIMEOUT_MS,
  parseResponse,
} from "@softmax/observatory-client/transport";
import { withRequestDeadline } from "@softmax/observatory-client/request-signal";

export const sessionChangeEvent = "softmax:session-change";
export const sessionChannel = "next-auth";

export function announceSessionChange({
  kind = "changed",
}: {
  readonly kind?: ("changed" | "signed-out" | "invalidated") | undefined;
}) {
  window.dispatchEvent(new CustomEvent(sessionChangeEvent, { detail: kind }));
  const channel = new BroadcastChannel(sessionChannel);
  const message = {
    event: "session",
    data: {
      trigger: {
        changed: "getSession",
        "signed-out": "signout",
        invalidated: "invalidated",
      }[kind],
    },
  };
  // oxlint-disable-next-line unicorn/require-post-message-target-origin -- BroadcastChannel messages stay on this origin and take no target origin.
  channel.postMessage(message);
  channel.close();
}

export async function signIn({
  provider,
  callbackUrl = window.location.href,
  authorizationParams,
}: {
  readonly provider: "google" | "github" | "discord";
  readonly callbackUrl?: string;
  readonly authorizationParams?: Record<string, string>;
}): Promise<void> {
  const { csrfToken } = await fetch("/api/auth/csrf", {
    cache: "no-store",
    signal: withRequestDeadline(undefined, DEFAULT_BACKEND_FETCH_TIMEOUT_MS),
  }).then((response) =>
    parseResponse<{
      readonly csrfToken: string;
    }>(response),
  );
  const parameters = new URLSearchParams(authorizationParams);
  const response = await fetch(`/api/auth/signin/${provider}?${parameters}`, {
    method: "POST",
    signal: withRequestDeadline(undefined, DEFAULT_BACKEND_FETCH_TIMEOUT_MS),
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-Auth-Return-Redirect": "1",
    },
    body: new URLSearchParams({ csrfToken, callbackUrl }),
  });
  const { url } = await parseResponse<{
    readonly url: string;
  }>(response);
  window.location.assign(url);
}

export async function signOut({
  callbackUrl = window.location.href,
}: {
  readonly callbackUrl?: string;
} = {}): Promise<void> {
  const { csrfToken } = await fetch("/api/auth/csrf", {
    cache: "no-store",
    signal: withRequestDeadline(undefined, DEFAULT_BACKEND_FETCH_TIMEOUT_MS),
  }).then((response) =>
    parseResponse<{
      readonly csrfToken: string;
    }>(response),
  );
  const response = await fetch("/api/auth/signout", {
    method: "POST",
    signal: withRequestDeadline(undefined, DEFAULT_BACKEND_FETCH_TIMEOUT_MS),
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-Auth-Return-Redirect": "1",
    },
    body: new URLSearchParams({ csrfToken, callbackUrl }),
  });
  const { url } = await parseResponse<{
    readonly url: string;
  }>(response);
  announceSessionChange({ kind: "signed-out" });
  window.location.assign(url);
}
